import { defineStore } from 'pinia'
import { collection, getDocs, updateDoc, doc, query, where, getDoc, writeBatch, onSnapshot } from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { db } from '@/config/firebase'
import { useAuthStore } from './auth'

type SyncStatus = 'not_started' | 'pending' | 'syncing' | 'completed' | 'failed';

interface Product {
  id: string
  title: string
  price: number
  product_type: string
  image: string
  tags: string[]
  html: string
  handle: string
  org_id: string
  available: boolean
  created_at: Date
  updated_at: Date
  config: {
    is_active: boolean
    priority: number
    meta_prompt?: string
  }
  inventory_qty: number
  isDirty?: boolean
  isSaving?: boolean
}

export const useProductStore = defineStore('products', {
      state: () => ({
    products: [] as Product[],
    lastSyncTime: null as Date | null,
    syncStatus: 'not_started' as SyncStatus,
    currentOrgId: null as string | null,
    unsubscribeSyncStatus: null as (() => void) | null,
    unsubscribeProducts: null as (() => void) | null
  }),

  getters: {
    sortedProducts: (state) => {
      return [...state.products].sort((a, b) => (b.config.priority || 0) - (a.config.priority || 0))
    },
    
    hasUnsavedChanges: (state) => {
      return state.products.some(p => p.isDirty)
    },

    isSyncing: (state) => {
      return state.syncStatus === 'pending' || state.syncStatus === 'syncing'
    },

    // Removed isShopifyConnected getter - now using org store
  },

  actions: {
    cleanupSubscriptions() {
      // Cleanup sync status subscription
      if (this.unsubscribeSyncStatus) {
        try {
          this.unsubscribeSyncStatus()
        } catch (error) {
          console.error('Error cleaning up sync subscription:', error)
        }
        this.unsubscribeSyncStatus = null
      }

      // Cleanup products subscription
      if (this.unsubscribeProducts) {
        try {
          this.unsubscribeProducts()
        } catch (error) {
          console.error('Error cleaning up products subscription:', error)
        }
        this.unsubscribeProducts = null
      }
    },

    async fetchOrgId() {
      const authStore = useAuthStore()
      if (!authStore.user?.uid) {
        this.cleanupSubscriptions()
        this.currentOrgId = null
        return null
      }

      const userDoc = await getDoc(doc(db, 'users', authStore.user.uid))
      if (userDoc.exists()) {
        const orgId = userDoc.data().org_id
        if (this.currentOrgId !== orgId) {
          this.currentOrgId = orgId
          // Reset products when org changes
          this.products = []
          this.watchSyncStatus()
        }
        return orgId
      }
      return null
    },

    watchSyncStatus() {
      // Clean up existing subscriptions if any
      this.cleanupSubscriptions()

      if (!this.currentOrgId) return

      try {
        // Subscribe to org config document
        const configRef = doc(db, 'config', this.currentOrgId)
        this.unsubscribeSyncStatus = onSnapshot(
          configRef,
          (doc) => {
            if (!doc.exists()) {
              console.warn('Config document does not exist for org:', this.currentOrgId)
              return
            }

            const data = doc.data()
            const newStatus = data.sync_status || 'not_started'
            const newSyncTime = data.sync_updated_at ? data.sync_updated_at.toDate() : null
            const prevStatus = this.syncStatus

            // Update sync status if changed
            if (prevStatus !== newStatus) {
              this.syncStatus = newStatus
              
              // Only refresh products when transitioning from syncing/pending to completed
              if (newStatus === 'completed' && 
                  (prevStatus === 'syncing' || prevStatus === 'pending')) {
                console.log('Sync completed, refreshing products...')
                this.fetchProducts().catch((error: Error) => {
                  console.error('Error refreshing products after sync:', error)
                })
              }
            }
            
            // Update sync time if changed
            if (newSyncTime && (!this.lastSyncTime || newSyncTime.getTime() !== this.lastSyncTime.getTime())) {
              this.lastSyncTime = newSyncTime
            }
          },
          (error: Error) => {
            console.error('Error watching sync status:', error)
            // Attempt to reestablish connection after error
            setTimeout(() => this.watchSyncStatus(), 5000)
          }
        )
      } catch (error) {
        console.error('Error setting up sync status subscription:', error)
      }
    },

    async watchProducts() {
      const orgId = await this.fetchOrgId()
      if (!orgId) return

      // Clean up existing subscription
      if (this.unsubscribeProducts) {
        this.unsubscribeProducts()
      }

      const q = query(
        collection(db, 'products'),
        where('org_id', '==', orgId)
      )
      
      try {
        this.unsubscribeProducts = onSnapshot(q, (snapshot) => {
          // Handle initial load and updates
          this.products = snapshot.docs.map(doc => ({
            ...doc.data(),
            id: doc.id,
            created_at: doc.data().created_at?.toDate(),
            updated_at: doc.data().updated_at?.toDate(),
            isDirty: false,
            isSaving: false
          })) as Product[]
        }, (error) => {
          console.error('Error watching products:', error)
          // Attempt to reestablish connection after error
          setTimeout(() => this.watchProducts(), 5000)
        })
      } catch (error) {
        console.error('Error setting up products subscription:', error)
      }
    },

    // Keep fetchProducts for initial load and manual refresh
    async fetchProducts() {
      await this.watchProducts()
    },

    async updateProduct(productId: string, data: Partial<Product>) {
      // Find product in state
      const index = this.products.findIndex(p => p.id === productId)
      if (index === -1) return

      const currentProduct = this.products[index]
      
      // Check if values actually changed
      const hasChanges = Object.entries(data).some(([key, value]) => {
        return key !== 'isDirty' && 
               key !== 'isSaving' &&
               value !== currentProduct[key as keyof Product]
      })

      // Only mark as dirty if there are actual changes
      if (hasChanges) {
        this.products[index] = {
          ...currentProduct,
          ...data,
          isDirty: true
        }
      }
    },

    async saveProduct(productId: string) {
      const product = this.products.find(p => p.id === productId)
      if (!product || !product.isDirty) return

      try {
        // Set saving state
        product.isSaving = true

        // Prepare data for Firestore (remove client-only fields)
        const { isDirty, isSaving, ...productData } = product
        
        // Update Firestore
        const productRef = doc(db, 'products', productId)
        await updateDoc(productRef, {
          ...productData,
          updated_at: new Date()
        })

        // Update local state
        product.isDirty = false
        product.updated_at = new Date()
      } catch (error) {
        console.error('Error saving product:', error)
        throw error
      } finally {
        product.isSaving = false
      }
    },

    async saveAllProducts() {
      const dirtyProducts = this.products.filter(p => p.isDirty)
      await Promise.all(dirtyProducts.map(p => this.saveProduct(p.id)))
    },

    async batchUpdateProducts(productIds: string[], updates: Partial<Product>, onProgress?: (current: number, total: number) => void) {
      try {
        const batchSize = 500; // Firestore batch limit
        const batches = [];
        let currentBatch = writeBatch(db);
        let operationsInCurrentBatch = 0;
        let totalProcessed = 0;

        for (const productId of productIds) {
          // Update local state
          const index = this.products.findIndex(p => p.id === productId);
          if (index === -1) continue;

          // Update Firestore batch
          const productRef = doc(db, 'products', productId);
          currentBatch.update(productRef, {
            ...updates,
            updated_at: new Date()
          });

          const currentProduct = this.products[index];
          
          // Check if values actually changed
          const hasChanges = Object.entries(updates).some(([key, value]) => {
            return key !== 'isDirty' && 
                   key !== 'isSaving' &&
                   value !== currentProduct[key as keyof Product]
          });

          // Update local state immediately
          if (hasChanges) {
            this.products[index] = {
              ...currentProduct,
              ...updates,
              updated_at: new Date(),
              isDirty: true
            };
          }

          operationsInCurrentBatch++;
          
          // If we've reached the batch limit, commit and start a new batch
          if (operationsInCurrentBatch === batchSize) {
            batches.push(currentBatch.commit());
            currentBatch = writeBatch(db);
            operationsInCurrentBatch = 0;
            
            totalProcessed += batchSize;
            onProgress?.(totalProcessed, productIds.length);
          }
        }

        // Commit any remaining operations
        if (operationsInCurrentBatch > 0) {
          batches.push(currentBatch.commit());
          totalProcessed += operationsInCurrentBatch;
          onProgress?.(totalProcessed, productIds.length);
        }

        // Wait for all batches to complete
        await Promise.all(batches);

        return true;
      } catch (error) {
        console.error('Error in batch update:', error);
        throw error;
      }
    },

    async syncWithShopify() {
      if (this.isSyncing) {
        return { 
          success: true, 
          sync_status: this.syncStatus, 
          message: 'Sync already in progress' 
        }
      }

      try {
        const functions = getFunctions(undefined, 'australia-southeast1')
        const syncProducts = httpsCallable<void, { success: boolean; message: string }>(functions, 'syncProducts')
        
        const result = await syncProducts()
        if (!result.data.success) {
          throw new Error(result.data.message || 'Failed to sync products')
        }

        return { 
          success: true, 
          sync_status: this.syncStatus,
          message: result.data.message 
        }
      } catch (error) {
        console.error('Error syncing products:', error)
        throw error
      }
    }
  }
}) 